import React, { useState } from 'react'
import {
  Box,
  Card,
  Checkbox,
  Flex,
  FormField,
  FormFieldLabelPosition,
  PrimaryButton,
  SecondaryButton,
  space,
  TextInput
} from '@workday/canvas-kit-react'
import ConfigureFeedAgenciesSection from './ConfigureFeedAgenciesSection'
import ConfigureFeedIncidentTypesSection from './ConfigureFeedIncidentTypesSection'
import { RespondAppConfiguration } from '../models/RespondAppConfiguration'
import FeedConfiguration from '../models/FeedConfiguration'
import { PrimaryButtonColors } from '../../components/CanvasThemes'
import { CenteredLayout } from '../../components/CenteredLayout'
import { callTypeFromCode } from '../../models/CallType'

export type ConfigureFeedProps = {
  appConfiguration: RespondAppConfiguration
  configuration: FeedConfiguration
  onConfigureFeed: (feedConfiguration: FeedConfiguration) => void
  onCancel: () => void
}

const ConfigureFeed = ({ appConfiguration, configuration, onConfigureFeed, onCancel }: ConfigureFeedProps) => {
  const [agencyIDs, setAgencyIDs] = useState(configuration.agencyIDs)
  const [callTypes, setCallTypes] = useState(configuration.callTypes)
  const [name, setName] = useState(configuration.name)
  const [saved, setSaved] = useState(true)
  const [hideMedical, setHideMedical] = useState(configuration.hideMedical)
  const [hideRecent, setHideRecent] = useState(configuration.hideRecent)
  const [showFullPSAP, setShowFullPSAP] = useState(configuration.showFullPSAP)

  const feedType = configuration.type
  return (
    <CenteredLayout>
      <Card>
        <Card.Heading>{configuration.type === 'SAVED' ? 'Saved Feed Settings' : 'Feed Settings'}</Card.Heading>
        {appConfiguration.context === 'PUBLIC' && !appConfiguration.isEmbedded && (
          <ConfigureFeedAgenciesSection
            agencyIDs={agencyIDs}
            onChangeAgencyIDs={agencyIDs => setAgencyIDs(agencyIDs)}
          />
        )}
        {appConfiguration.context === 'PUBLIC' && (
          <ConfigureFeedIncidentTypesSection
            callTypeIDs={callTypes.map(callType => callType.id)}
            onChangeCallTypeIDs={callTypeIDs =>
              setCallTypes(callTypeIDs.map(id => callTypeFromCode(id)).filter(callType => callType.id !== 'UNK'))
            }
          />
        )}
        {appConfiguration.context === 'PUBLIC' && (
          <FormField label="Show Medical Incidents" labelPosition={FormFieldLabelPosition.Left}>
            <Checkbox checked={!hideMedical} onChange={event => setHideMedical(!event.currentTarget.checked)} />
          </FormField>
        )}
        {appConfiguration.context === 'PUBLIC' && (
          <FormField label="Show Recent Incidents" labelPosition={FormFieldLabelPosition.Left}>
            <Checkbox checked={!hideRecent} onChange={event => setHideRecent(!event.currentTarget.checked)} />
          </FormField>
        )}
        {appConfiguration.context !== 'PUBLIC' && (
          <FormField label="Show Full PSAP" labelPosition={FormFieldLabelPosition.Left}>
            <Checkbox checked={showFullPSAP} onChange={event => setShowFullPSAP(event.currentTarget.checked)} />
          </FormField>
        )}
      </Card>
      {appConfiguration.context === 'PUBLIC' && !appConfiguration.isEmbedded &&
        <Card>
          {feedType === 'NEW' && (
            <FormField label="Add to Saved Feeds" labelPosition={FormFieldLabelPosition.Left}>
              <Checkbox checked={saved} onChange={event => setSaved(event.currentTarget.checked)} />
            </FormField>
          )}
          {name.length > 0 || saved && (
            <FormField label="Name">
              <TextInput value={name} onChange={event => setName(event.currentTarget.value)} />
            </FormField>
          )}
        </Card>
      }
      <Box>
        <Flex flexDirection="row" gap={space.xs}>
          <PrimaryButton
            colors={PrimaryButtonColors}
            disabled={appConfiguration.context === 'PUBLIC' && agencyIDs.length === 0}
            onClick={event => {
              onConfigureFeed(
                new FeedConfiguration(
                  configuration.id,
                  name,
                  agencyIDs,
                  configuration.token,
                  callTypes,
                  configuration.environment,
                  saved ? 'SAVED' : 'NEW',
                  hideMedical,
                  hideRecent,
                  showFullPSAP
                )
              )
            }}
          >
            {saved ? 'Save and View' : 'View'}
          </PrimaryButton>
          <SecondaryButton onClick={event => onCancel()}>Cancel</SecondaryButton>
        </Flex>
      </Box>
    </CenteredLayout>
  )
}

export default ConfigureFeed
